import React from 'react';

const STATUS_STYLES = {
  OPEN:      'bg-green-100 text-green-700',
  AGREED:    'bg-yellow-100 text-yellow-700',
  APPROVED:  'bg-blue-100 text-blue-700',
  REJECTED:  'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
};

const STATUS_LABELS = {
  OPEN:      'Open',
  AGREED:    'Agreed — Pending SSL',
  APPROVED:  'Approved',
  REJECTED:  'Rejected',
  CANCELLED: 'Cancelled',
};

const TYPE_STYLES = {
  D: 'bg-gray-100 text-gray-600',
  E: 'bg-blue-100 text-blue-700',
  H: 'bg-orange-100 text-orange-700',
};
const TYPE_LABELS = { D: 'Weekday', E: 'Weekend', H: 'Heavy Day' };

const ROLE_STYLES = {
  ON_CALL: 'bg-indigo-100 text-indigo-700',
  SITTER:  'bg-yellow-100 text-yellow-700',
};
const ROLE_LABELS = { ON_CALL: 'On Call', SITTER: 'Sitter' };

function formatDate(iso) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

// ── Card ─────────────────────────────────────────────────────────────────────

export default function SwapListingCard({ listing, currentUserId, isSSL, onClaim, onCancel, onApprove, onReject }) {
  const {
    id, status, posted_by, posted_by_detail,
    claimed_by_detail, shift_detail,
    location_name, shift_type,
  } = listing;

  const shiftDate = shift_detail?.date;
  const role      = shift_detail?.role;
  const isOwner   = posted_by === currentUserId;
  const isClaimer = listing.claimed_by === currentUserId;

  return (
    <div className={`rounded-xl border bg-white shadow-sm transition-shadow hover:shadow-md ${
      status === 'CANCELLED' || status === 'APPROVED' ? 'opacity-60' : ''
    }`}>
      <div className="p-4 space-y-3">

        {/* Header row */}
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1">
            <p className="text-base font-semibold text-gray-900">
              {shiftDate ? formatDate(shiftDate) : '—'}
            </p>
            <p className="text-xs text-gray-400">
              {location_name ?? '—'} · Posted by{' '}
              <span className="font-medium text-gray-600">
                {isOwner ? 'You' : `${posted_by_detail?.first_name} ${posted_by_detail?.last_name}`}
              </span>
            </p>
          </div>

          {/* Status badge */}
          <span className={`flex-shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[status] ?? ''}`}>
            {STATUS_LABELS[status] ?? status}
          </span>
        </div>

        {/* Shift type + role chips */}
        <div className="flex flex-wrap gap-2">
          {shift_type && (
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${TYPE_STYLES[shift_type]}`}>
              {TYPE_LABELS[shift_type]}
            </span>
          )}
          {role && (
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${ROLE_STYLES[role]}`}>
              {ROLE_LABELS[role]}
            </span>
          )}
        </div>

        {/* Claimed-by info */}
        {status === 'AGREED' && claimed_by_detail && (
          <p className="text-xs text-gray-500">
            Claimed by{' '}
            <span className="font-medium text-gray-700">
              {isClaimer ? 'You' : `${claimed_by_detail.first_name} ${claimed_by_detail.last_name}`}
            </span>
            {' '}— awaiting SSL approval
          </p>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2 pt-1">
          {/* SL: claim an open listing they don't own */}
          {status === 'OPEN' && !isOwner && !isSSL && (
            <ActionBtn color="green" label="Claim Shift" onClick={() => onClaim(id)} />
          )}

          {/* SL: cancel their own open listing */}
          {status === 'OPEN' && isOwner && (
            <ActionBtn color="red" label="Cancel Listing" onClick={() => onCancel(id)} />
          )}

          {/* SSL: approve / reject agreed swap */}
          {status === 'AGREED' && isSSL && (
            <>
              <ActionBtn color="green" label="Approve"    onClick={() => onApprove(id)} />
              <ActionBtn color="red"   label="Reject"     onClick={() => onReject(id)} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ label, onClick, color }) {
  const colors = {
    green: 'bg-green-600 hover:bg-green-700 text-white',
    red:   'bg-red-500   hover:bg-red-600   text-white',
  };
  return (
    <button
      onClick={onClick}
      className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${colors[color]}`}
    >
      {label}
    </button>
  );
}
